import { Box, Divider, Typography, useTheme } from '@mui/material';
import ActionButtons from 'components/action-buttons';
import Paper from 'components/paper';
import Mui from 'materials';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import DailyConsumptionRate from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/daily-consumption/daily-consumption-rate';
import EditDailyConsumption from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/daily-consumption/edit-daily-consumption';
import FeedStrategyDialog from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/daily-consumption/feed-strategy-dialog';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { useUpdateOFDSettings } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/hooks/use-update-ofd-settings';
import { ConsumptionAdjustmentMethod } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/consumption-adjustment-method.enum';
import { UpdateConsumptionRateInput } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings-input.type';
import { HeaderWrapper } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/styled-components';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.inputSettings.dailyConsumptionRateTab';

type Props = {};

const DailyConsumptionRateTab: React.FC<Props> = () => {
  const theme = useTheme();

  const {
    getOfdSettingsQuery: { data: ofdSettings, isFetching: isOfdSettingsFetching, refetch: refetchOfdSettings },
    dailyConsumptionInput,
  } = useInputSettingsState();

  const { createInputSettingsEvent } = useGoogleTagManager();
  const { updateDailyConsumptionRate } = useUpdateOFDSettings();

  const [edit, setEdit] = useState(false);

  const handleCancel = () => {
    setEdit(false);
  };

  const handleEdit = (edited: boolean) => setEdit(edited);

  const handleSave = () => {
    if (!dailyConsumptionInput) return;

    updateDailyConsumptionRate.mutate(dailyConsumptionInput, {
      onSuccess: () => {
        if (ofdSettings) {
          let oldValue2 = '';
          let newValue2 = '';

          switch (dailyConsumptionInput?.consumptionRateAdjustmentMethod) {
            case ConsumptionAdjustmentMethod.historicalAverage:
              oldValue2 = String(ofdSettings.consumptionRateAdjustmentDays);
              newValue2 = String(dailyConsumptionInput.consumptionRateAdjustmentDays);
              break;
            case ConsumptionAdjustmentMethod.manualCoefficient:
              oldValue2 = String(ofdSettings.manualConsumptionRateCoefficient ?? '1');
              newValue2 = String(dailyConsumptionInput.manualConsumptionRateCoefficient);
              break;
            case ConsumptionAdjustmentMethod.historicalAverageIgnoreStarvation:
              oldValue2 = String(ofdSettings.consumptionRateAdjustmentDays);
              newValue2 = String(dailyConsumptionInput.consumptionRateAdjustmentDays);
              break;
          }

          createInputSettingsEvent('save_daily_consumption_rate', {
            oldValue1: ofdSettings.consumptionRateAdjustmentMethod as ConsumptionAdjustmentMethod,
            newValue1: dailyConsumptionInput?.consumptionRateAdjustmentMethod as ConsumptionAdjustmentMethod,
            oldValue2: oldValue2,
            newValue2: newValue2,
            section: 'inputSettings',
          });
          refetchOfdSettings();
          setEdit(false);
        }
      },
    });
  };

  return (
    <Paper>
      <Box display={'flex'} minHeight={600} flexDirection={'column'} gap={4} p={4}>
        <HeaderWrapper>
          <Box display={'flex'} flexDirection={'column'} gap={2}>
            <Typography variant="h3" fontWeight={700}>
              <FormattedMessage id={`${PAGE_NAME_ID}.label`} />
            </Typography>
            <Typography variant="body1">
              <FormattedMessage id={`${PAGE_NAME_ID}.headerText`} />
            </Typography>
          </Box>

          <Box display={'flex'} gap={4}>
            <ActionButtons
              isEditing={edit}
              onEdit={handleEdit}
              onCancel={handleCancel}
              onSave={handleSave}
              isLoading={isOfdSettingsFetching}
            />
          </Box>
        </HeaderWrapper>
        {edit ? <EditDailyConsumption /> : <DailyConsumptionRate />}
        <Divider flexItem />
        <FeedStrategyDialog />
      </Box>
    </Paper>
  );
};

export default DailyConsumptionRateTab;
